import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../store/authStore';
import { updateUserEmail, updateUserPassword, isPasswordSecure, deleteUserAccount } from '../lib/auth';
import { 
  BookUser, User, Mail, MapPin, Calendar, Edit2, LogOut, Shield, 
  Instagram, Youtube, Globe, Music, AlertCircle, CheckCircle, 
  XCircle, Briefcase, Lock, MessageSquare, MessageCircleQuestion, Trash2,
  Send, ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';
import Button from '../components/Button';
import Card from '../components/Card';
import UserName from '../components/UserName';
import AvatarUploadProfile from '../components/AvatarUploadProfile';
import supabase from '../lib/supabase';
import Avatar from '../components/Avatar';

// Enhanced password validation schema
const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .refine(
    (password) => /[a-z]/.test(password),
    { message: 'Password must contain at least one lowercase letter' }
  )
  .refine(
    (password) => /[A-Z]/.test(password),
    { message: 'Password must contain at least one uppercase letter' }
  )
  .refine(
    (password) => /\d/.test(password),
    { message: 'Password must contain at least one digit' }
  )
  .refine(
    (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    { message: 'Password must contain at least one special character' }
  );

// Update the securitySchema to handle email and password separately
const securitySchema = z.object({
  email: z.string().email('Invalid email address').optional(),
  currentPassword: z.string().optional(),
  newPassword: passwordSchema.optional(),
  confirmPassword: z.string().optional(),
}).refine(data => {
  // Only validate password fields if newPassword is provided
  if (data.newPassword) {
    if (!data.currentPassword) {
      return false;
    }
    if (data.newPassword !== data.confirmPassword) {
      return false;
    }
  }
  return true;
}, {
  message: "Current password is required and passwords must match",
  path: ["confirmPassword"],
});

// Schema for feedback/questions
const feedbackSchema = z.object({
  type: z.enum(['question', 'feedback']),
  content: z.string().min(10, 'Please provide more detail').max(1000, 'Maximum 1000 characters'),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

// Available professions list
const PROFESSIONS = [
  "Artist/Musician/Singer",
  "Songwriter",
  "Producer",
  "Instrumentalist",
  "Sound Engineer",
  "DJ/Performer",
  "Manager/Promoter",
  "Venue Representative",
  "Studio Owner/Manager",
  "Label Representative",
  "Other"
];

// Experience levels
const EXPERIENCE_LEVELS = [
  { value: "beginner", label: "Beginner/Starting Out" },
  { value: "intermediate", label: "Intermediate (2-5 years experience)" },
  { value: "professional", label: "Professional (5+ years experience)" },
  { value: "veteran", label: "Industry Veteran (10+ years experience)" }
];

function Profile() {
  const { user, verificationRequest, fetchVerificationRequest, submitVerificationRequest, updateProfile, signOut, isOAuth, checkAuthMethod } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formattedLocation, setFormattedLocation] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [securitySuccess, setSecuritySuccess] = useState<string | null>(null);
  const [questionContent, setQuestionContent] = useState('');
  const [feedbackContent, setFeedbackContent] = useState('');
  const [isSendingQuestion, setIsSendingQuestion] = useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [selectedProfessions, setSelectedProfessions] = useState<string[]>([]);
  const [isExperienceDropdownOpen, setIsExperienceDropdownOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is using OAuth
  useEffect(() => {
    const checkOAuth = async () => {
      await checkAuthMethod();
    };
    
    checkOAuth();
  }, [checkAuthMethod]);

  // If user is using OAuth, don't show security tab
  useEffect(() => {
    if (isOAuth && activeTab === 'security') {
      setActiveTab('profile');
    }
  }, [isOAuth, activeTab]);

  useEffect(() => {
    if (user?.id) {
      fetchVerificationRequest();
    }
  }, [user?.id, fetchVerificationRequest]);

  useEffect(() => {
    if (user?.location && user.location_enabled) {
      const parts = user.location.split(',').map(part => part.trim());
      if (parts.length >= 2) {
        const city = parts[0];
        const country = parts[1];
        setFormattedLocation(`${city}, ${country}`);
      } else {
        setFormattedLocation(user.location);
      }
    }
  }, [user?.location, user?.location_enabled]);

  // Initialize selected professions from user data
  useEffect(() => {
    if (user?.professions) {
      setSelectedProfessions(user.professions);
    }
  }, [user?.professions]);

  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors }, reset: resetProfile, watch: watchProfile } = useForm<any>({
    defaultValues: {
      full_name: user?.full_name || '',
      display_name: user?.display_name || '',
      bio: user?.bio || '',
      location: user?.location || '',
      location_enabled: user?.location_enabled || false,
      website: user?.website || '',
      professions: user?.professions || [],
      other_profession: user?.other_profession || '',
      experience_level: user?.experience_level || 'beginner',
      goals: user?.goals || '',
      social: user?.social || {},
    }
  });

  const { register: registerSecurity, handleSubmit: handleSecuritySubmit, formState: { errors: securityErrors }, watch: watchSecurity, reset: resetSecurity, setValue: setSecurityValue } = useForm({
    resolver: zodResolver(securitySchema),
    defaultValues: {
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  // Update email field when user email changes
  useEffect(() => {
    if (user?.email) {
      setSecurityValue('email', user.email);
    }
  }, [user?.email, setSecurityValue]);

  const newPassword = watchSecurity('newPassword', '');
  const passwordCheck = isPasswordSecure(newPassword || '');
  const selectedExperienceLevel = watchProfile('experience_level');

  const handleAvatarUpload = async (url: string) => {
    try {
      await updateProfile({ avatar_url: url });
    } catch (error) {
      console.error('Failed to update avatar:', error);
    }
  };

  const handleProfessionToggle = (profession: string) => {
    setSelectedProfessions(prev => {
      if (prev.includes(profession)) {
        return prev.filter(p => p !== profession);
      } else {
        return [...prev, profession];
      }
    });
  };

  const onSubmitProfile = async (data: any) => {
    try {
      setIsUpdating(true);

      let formattedLocationInput = data.location;
      if (formattedLocationInput) {
        const parts = formattedLocationInput.split(',').map(part => part.trim());
        if (parts.length >= 2) {
          formattedLocationInput = `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
        }
      }

      // Remove credentials from profile update
      const { credentials, ...profileData } = data;

      const updatedData = {
        ...profileData,
        location: formattedLocationInput,
        professions: selectedProfessions,
        social: {
          ...(user?.social || {}),
          ...(data.social || {}),
        },
        updated_at: new Date().toISOString(),
      };

      await updateProfile(updatedData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Separate handler for verification requests
  const handleVerificationSubmit = async (credentials: string) => {
    try {
      await submitVerificationRequest(credentials);
    } catch (error) {
      console.error('Failed to submit verification request:', error);
    }
  };

  const onSubmitSecurity = async (data: any) => {
    try {
      setIsUpdating(true);
      setSecurityError(null);
      setSecuritySuccess(null);

      // Handle email update
      if (data.email && data.email !== user?.email) {
        try {
          const result = await updateUserEmail(data.email);
          
          setSecuritySuccess(result.message);
          
          // Don't reset the email field here - keep showing the new email
          resetSecurity({ 
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            email: data.email // Keep the new email in the form
          });
        } catch (error: any) {
          setSecurityError(error.message || 'Failed to update email');
          return;
        }
      }

      // Handle password update
      if (data.newPassword) {
        try {
          const result = await updateUserPassword(data.currentPassword, data.newPassword);
          setSecuritySuccess(result.message);
          resetSecurity({ 
            ...data,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        } catch (error: any) {
          setSecurityError(error.message || 'Failed to update password');
          return;
        }
      }
    } catch (error: any) {
      console.error('Security update error:', error);
      setSecurityError(error.message || 'An error occurred while updating your security settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubmitContent = async (type: 'question' | 'feedback', content: string) => {
    if (!content.trim()) return;

    try {
      type === 'question' ? setIsSendingQuestion(true) : setIsSendingFeedback(true);
      setFeedbackError(null);
      
      const { error } = await supabase
        .from('user_feedback')
        .insert([
          {
            user_id: user?.id,
            type,
            content: content.trim()
          }
        ]);

      if (error) throw error;

      if (type === 'question') {
        setQuestionSuccess(true);
        setQuestionContent('');
        setTimeout(() => setQuestionSuccess(false), 3000);
      } else {
        setFeedbackSuccess(true);
        setFeedbackContent('');
        setTimeout(() => setFeedbackSuccess(false), 3000);
      }

    } catch (error: any) {
      console.error('Failed to submit:', error);
      setFeedbackError(error.message || 'Failed to submit. Please try again.');
    } finally {
      type === 'question' ? setIsSendingQuestion(false) : setIsSendingFeedback(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      setDeleteError(null);
      
      await deleteUserAccount(deleteConfirmText);
      
      // Sign out and redirect
      await signOut();
      navigate('/auth', { 
        state: { 
          success: 'Your account has been successfully deleted. Thank you for using our platform.' 
        }
      });
    } catch (error: any) {
      console.error('Account deletion failed:', error);
      setDeleteError(error.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    resetProfile();
    setSelectedProfessions(user?.professions || []);
  };

  const SpotifyLogo = () => (
    <svg width="20" height="20" viewBox="0 0 168 168" fill="#401c14">
      <path d="M83.996 0.277C37.747 0.277 0.253 37.77 0.253 84.019C0.253 130.27 37.747 167.76 83.996 167.76C130.25 167.76 167.74 130.27 167.74 84.019C167.74 37.773 130.25 0.281 83.995 0.281L83.996 0.277ZM122.4 121.057C120.9 123.517 117.68 124.297 115.22 122.787C95.558 110.777 70.806 108.057 41.656 114.717C38.847 115.357 36.047 113.597 35.407 110.787C34.764 107.977 36.517 105.177 39.333 104.537C71.233 97.249 98.596 100.387 120.67 113.877C123.13 115.387 123.91 118.597 122.4 121.057ZM132.65 98.255C130.76 101.327 126.74 102.297 123.67 100.407C101.16 86.571 66.847 82.564 40.222 90.646C36.769 91.689 33.122 89.743 32.074 86.296C31.034 82.843 32.981 79.203 36.428 78.153C66.841 68.925 104.65 73.395 130.5 89.28C133.57 91.17 134.54 95.19 132.65 98.26V98.255ZM133.53 74.511C106.54 58.48 62.01 57.006 36.241 64.827C32.103 66.082 27.727 63.746 26.473 59.608C25.219 55.468 27.553 51.095 31.694 49.837C61.275 40.857 110.45 42.592 141.524 61.039C145.254 63.248 146.474 68.055 144.264 71.772C142.064 75.494 137.244 76.721 133.534 74.511H133.53Z"/>
    </svg>
  );

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <User className="w-12 h-12 text-neutral mb-4" />
        <h2 className="text-xl font-bold font-dillan text-primary mb-2">Sign in to View Profile</h2>
        <p className="text-neutral text-sm mb-4">Access your personal profile and settings</p>
        <a
          href="/auth"
          className="px-6 py-2 bg-accent text-white rounded-xl font-medium hover:bg-accent-dark transition-colors"
        >
          Sign In
        </a>
      </div>
    );
  }
  
  return (
    <div className="px-4 pt-1 pb-8 bg-primary-dark">
      {/* Tab Navigation */}
      {/* <div className="flex flex-wrap justify-center bg-primary-dark rounded-xl p-1 gap-2 sm:gap-4 mb-6">
        <button
          className={`w-40 py-2 text-sm sm:text-sm rounded-full transition-colors font-dillan uppercase ${
            activeTab === 'profile'
              ? 'bg-secondary-light text-primary border-4 border-secondary-light'
              : 'text-neutral border-4 border-secondary-light hover:text-primary'
          }`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        {!isOAuth && (
          <button
            className={`w-40 py-2 text-sm sm:text-sm rounded-full transition-colors font-dillan uppercase ${
              activeTab === 'security'
                ? 'bg-secondary-light text-primary border-4 border-secondary-light'
                : 'text-neutral border-4 border-secondary-light hover:text-primary'
            }`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        )}
      </div> */}

      {activeTab === 'profile' && (
        <Card className="relative overflow-hidden bg-neutral mb-6 mt-6">
          {!isEditing && user && (
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={signOut}
                className="w-9 h-9 p-0 flex items-center justify-center text-surface-light"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          )}

          {isEditing ? (
            <form onSubmit={handleProfileSubmit(onSubmitProfile)}>
              {/* Profile Picture */}
              <div className="mb-4 mt-6">
                <AvatarUploadProfile
                  onUploadComplete={handleAvatarUpload}
                  initialImage={user.avatar_url}
                />
              </div>

              <div className="flex-1">
                <div className="flex items-center text-surface-light mb-4">
                  <span className="font-bold text-secondary-light mr-1 whitespace-nowrap">Full Name:</span>
                  <textarea
                    {...registerProfile('full_name')}
                    className="flex-1  border border-neutral bg-neutral text-surface-light placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none resize-none"
                    placeholder="Full Name"
                    rows={1}

                  />
                  {profileErrors.full_name && (
                    <p className="mt-1 text-sm text-secondary-dark">{profileErrors.full_name.message}</p>
                  )}
                </div>
                <div className="flex items-center text-surface-light mb-4">
                  <span className="font-bold text-secondary-light mr-1 whitespace-nowrap">Display Name:</span>
                  <textarea
                    {...registerProfile('display_name')}
                    className="flex-1  border border-neutral bg-neutral text-surface-light placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none resize-none"
                    placeholder="Display Name"
                    rows={1}
                  />
                  {profileErrors.display_name && (
                    <p className="mt-1 text-sm text-secondary-dark">{profileErrors.display_name.message}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center text-surface-light mb-4">
                  
                
                  <span className="font-bold text-secondary-light mr-1 whitespace-nowrap">Bio:</span>
                
                  <textarea
                    {...registerProfile('bio')}
                    className="flex-1 border border-neutral bg-neutral text-surface-light placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none resize-none"
                    placeholder="Tell us about yourself"
                    rows={1}
                  />
                </div>



              {/* Professions Field */}
              <div>
               
                <div className="space-y-2 bg-surface-light p-4 rounded-xl">
                  {PROFESSIONS.map((profession) => (
                    <label key={profession} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedProfessions.includes(profession)}
                        onChange={() => handleProfessionToggle(profession)}
                        className="w-4 h-4 pl-2 border-2 border-neutral rounded-full 
      appearance-none transition-all 
      checked:bg-neutral checked:border-neutral"
                      />
                      <span className="text-neutral">{profession}</span>
                    </label>
                  ))}
                </div>
                {profileErrors.professions && (
                  <p className="mt-1 text-sm text-secondary-dark">{profileErrors.professions.message}</p>
                )}
              </div>

              {/* Other Profession Field - shown only when "Other" is selected */}
              {selectedProfessions.includes('Other') && (
                <div>
                  <input
                    type="text"
                    {...registerProfile('other_profession')}
                    className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-surface-light focus:ring-0 focus:outline-none mt-1"
                    placeholder="Specify your other profession"
                  />
                </div>
              )}

              <div className="mb-3 mt-4">
                <textarea
                  {...registerProfile('goals')}
                  className="w-full h-16 px-4 py-2 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-surface-light focus:ring-0 focus:outline-none"
                  placeholder="What are your goals in the music industry?"
                  rows={2}
                />
              </div>

              {/* Experience Level Dropdown */}
              <div className="mb-4">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsExperienceDropdownOpen(!isExperienceDropdownOpen)}
                    className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral focus:outline-none flex items-center justify-between"
                  >
                    <span>
                      {EXPERIENCE_LEVELS.find(level => level.value === selectedExperienceLevel)?.label || 'Select Experience Level'}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${isExperienceDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {isExperienceDropdownOpen && (
                    <div className="absolute z-10 w-full bg-surface-light border border-neutral rounded-xl shadow-lg">
                      <div className="py-2">
                        {EXPERIENCE_LEVELS.map((level) => (
                          <label
                            key={level.value}
                            className="flex items-center px-4 py-2 hover:bg-neutral cursor-pointer"
                          >
                            <input
                              type="radio"
                              {...registerProfile('experience_level')}
                              value={level.value}
                              checked={selectedExperienceLevel === level.value}
                              onChange={(e) => {
                                registerProfile('experience_level').onChange(e);
                                setIsExperienceDropdownOpen(false);
                              }}
                              className="w-4 h-4 border-2 border-neutral rounded-full appearance-none transition-all
                                checked:bg-neutral checked:border-neutral 
                                hover:border-surface-light hover:checked:bg-surface-light"
                            />
                            <span className="text-neutral hover:text-surface-light pl-2">{level.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>


              {/* Goals */}
            

              <div>
                <div>
                  <input
                    {...registerProfile('location')}
                    className=" w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-surface-light focus:ring-0 focus:outline-none mb-2"
                    placeholder="City, Country"
                  />

                </div>
                <label className="flex items-center space-x-3 pl-2 mb-4">
                  <input
                    type="checkbox"
                    {...registerProfile('location_enabled')}
                    className="w-4 h-4 pl-2 border-2 border-surface-light rounded-full 
      appearance-none transition-all 
      checked:bg-surface-light checked:border-surface-light"
                  />
                  <span className="text-sm text-surface-light">Show my location on profile</span>
                </label>
              </div>

              <div className="mb-6">
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Globe className="w-5 h-5 text-primary" />
                    <input
                      {...registerProfile('website')}
                      className="flex-1 h-12 px-4 rounded-full border border-secondary-light bg-secondary-light text-neutral placeholder:text-neutral focus:border-secondary-light focus:ring-0 focus:outline-none"
                      placeholder="Website URL"
                    />
                  </div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Instagram className="w-5 h-5 text-primary" />
                    <input
                      {...registerProfile('social.instagram')}
                      className="flex-1 h-12 px-4 rounded-full border border-secondary-light bg-secondary-light text-neutral placeholder:text-neutral focus:border-secondary-light focus:ring-0 focus:outline-none"
                      placeholder="Instagram username"
                    />
                  </div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Youtube className="w-5 h-5 text-primary" />
                    <input
                      {...registerProfile('social.youtube')}
                      className="flex-1 h-12 px-4 rounded-full border border-secondary-light bg-secondary-light text-neutral placeholder:text-neutral focus:border-secondary-light focus:ring-0 focus:outline-none"
                      placeholder="YouTube channel URL"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <SpotifyLogo className="w-5 h-5 bg-surface-light"/>
                    <input
                      {...registerProfile('social.spotify')}
                      className="flex-1 h-12 px-4 rounded-full border border-secondary-light bg-secondary-light text-neutral placeholder:text-neutral focus:border-secondary-light focus:ring-0 focus:outline-none"
                      placeholder="Spotify profile URL"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 text-surface-light"
                  onClick={cancelEdit}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="outline"
                  className="flex-1 text-surface-light"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Avatar
                    src={user.avatar_url}
                    alt={user.display_name || user.full_name}
                    size="xl"
                  />
                </div>
                <div className="flex-1">
                  <UserName 
                    name={user.display_name || user.full_name}
                    verified={user.verified}
                    size="lg"
                    className="mb-1 text-secondary-light"
                  />
                  {(user.website || user.social?.instagram || user.social?.youtube || user.social?.spotify) && (
                  <div className="flex items-center space-x-4">
                    {user.website && (
                      <a
                        href={user.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-accent transition-colors"
                        title="Website"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                    {user.social?.instagram && (
                      <a
                        href={`https://instagram.com/${user.social.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-accent transition-colors"
                        title="Instagram"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {user.social?.youtube && (
                      <a
                        href={user.social.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-accent transition-colors"
                        title="YouTube"
                      >
                        <Youtube className="w-6 h-6" />
                      </a>
                    )}
                    {user.social?.spotify && (
                      <a
                        href={user.social.spotify}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-accent transition-colors "
                        title="Spotify"
                      >
                        <SpotifyLogo className="w-5 h-5 bg-surface-light"/>
                      </a>
                    )}
                  </div>
                )}
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {user.bio && (
                  <div className="flex items-start text-surface-light">
                    <BookUser className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-secondary-light">Bio: </span>
                      <span className="text-surface-light text-md ">{user.bio}</span>
                    </div>
                  </div>
                )}
                {/* Display Professions */}
                {user.professions && user.professions.length > 0 && (
                  <div className="flex items-start text-surface-light">
                    <Briefcase className="w-5 h-5 mr-2 mt-0.5 text-primary flex-shrink-0" />
                    <div>
                      <span className="font-bold text-secondary-light">Profession(s): </span>
                      <span>
                        {user.professions
                          .map((profession) =>
                            profession === 'Other' && user.other_profession
                              ? `Other: ${user.other_profession}`
                              : profession
                          )
                          .join(', ')}
                      </span>
                    </div>
                  </div>
                )}

                

                <div className="flex justify-between items-center">
                  {user.location_enabled && formattedLocation && (
                    <div className="flex items-center text-surface-light">
                      <MapPin className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-bold text-secondary-light">Location: </span>
                        <span>{formattedLocation}</span>
                      </div>
                    </div>
                  )}
                  <Button
                      onClick={() => setIsEditing(true)}
                      className="p-1 w-8 h-8 bg-neutral border-0 border-neutral text-surface-light hover:bg-neutral hover:border-neutral hover:text-primary"
                    >
                      <Edit2 className="w-5 h-5" />
                    </Button>
                </div>
                
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'security' && !isOAuth && (
        <Card className="bg-neutral mb-6">
          <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Security Settings</h2>
          
          {securityError && (
            <div className="mb-4 p-3 rounded-lg bg-secondary-dark bg-opacity-10 text-base text-sm flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{securityError}</span>
            </div>
          )}
          
          {securitySuccess && (
            <div className="mb-4 p-3 rounded-lg bg-accent bg-opacity-10 text-accent text-sm flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{securitySuccess}</span>
            </div>
          )}
          
          <form onSubmit={handleSecuritySubmit(onSubmitSecurity)} className="space-y-4">
            <div>
              <h3 className="text-md font-semibold text-primary mb-4 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Update Email Address
              </h3>
              <div className="flex gap-2">
                <input
                  type="email"
                  {...registerSecurity('email')}
                  className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-eutral focus:ring-0 focus:outline-none"
                  placeholder="your@email.com"
                />
                
              </div>
              {securityErrors.email && (
                <p className="mt-1 text-sm text-secondary-dark">{securityErrors.email.message}</p>
              )}
              <p className="mt-1 text-xs text-secondary-light px-4 mb-6">
                Changing your email will require verification from both your current and new email addresses.
              </p>
              <div className="flex justify-end items-center">
                <Button
                    type="button"
                    variant="outline"
                    className="w-24 text-surface-light"
                    disabled={isUpdating}
                    onClick={async () => {
                      const email = watchSecurity('email');
                      if (!email || email === user?.email) return;
                      
                      try {
                        setIsUpdating(true);
                        setSecurityError(null);
                        setSecuritySuccess(null);
                        
                        const result = await updateUserEmail(email);
                        
                        setSecuritySuccess(result.message);
                        
                        // Don't reset the email field here - keep showing the new email
                        resetSecurity({ 
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                          email: email // Keep the new email in the form
                        });
                      } catch (error: any) {
                        setSecurityError(error.message || 'Failed to update email');
                      } finally {
                        setIsUpdating(false);
                      }
                    }}
                  >
                    Save
                  </Button>
              </div>
            </div>
            
            <div className="border-t border-primary pt-6">
              <h3 className="text-md font-semibold text-primary mb-4 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </h3>
              
              <div className="space-y-4">
                <div>
                  
                  <input
                    type="password"
                    {...registerSecurity('currentPassword')}
                    className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-eutral focus:ring-0 focus:outline-none"
                    placeholder="Enter current password"
                  />
                </div>
                
                <div>
                  
                  <input
                    type="password"
                    {...registerSecurity('newPassword')}
                    className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-eutral focus:ring-0 focus:outline-none"
                    placeholder="Enter new password"
                  />
                  
                  {newPassword && (
                    <div className={`mt-2 px-2 text-sm ${passwordCheck.isValid ? 'text-surface-light' : 'text-surface-light'}`}>
                      <div className="flex items-center">
                        {passwordCheck.isValid ? (
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mr-1.5" />
                        )}
                        {passwordCheck.message}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                 
                  <input
                    type="password"
                    {...registerSecurity('confirmPassword')}
                    className="w-full h-12 px-4 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-eutral focus:ring-0 focus:outline-none mb-2"
                    placeholder="Confirm new password"
                  />
                  {securityErrors.confirmPassword && (
                    <p className="mt-1 px-4 text-sm text-surface-light">{securityErrors.confirmPassword.message}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end items-center">
              <Button
                type="submit"
                variant="outline"
                disabled={isUpdating}
                className="w-24"
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {activeTab === 'profile' && (
        <>
          <Card className="bg-neutral mb-6 mt-6">
            <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Verification Status</h2>
            <p className="text-surface-light mb-4">
              We offer each professional the opportunity to get manually verified by our team, 
              in order to provide a proof of legitimacy to others.
            </p>
    
            {user.verified && (
              <div className="bg-surface-light rounded-full p-4 mb-6">
                <p className="text-neutral text-sm">
                  Your account is verified. 
                </p>
              </div>
            )}
    
            {!user.verified && verificationRequest?.status === 'approved' && (
              <div className="bg-surface-light rounded-full p-4 mb-6">
                <p className="text-neutral text-sm">
                  Your verification request has been approved! Your account will be updated shortly.
                </p>
              </div>
            )}
    
            {!user.verified && !verificationRequest && (
              <form onSubmit={handleProfileSubmit((data) => handleVerificationSubmit(data.credentials))}>
                <div>
                  
                  <textarea
                    {...registerProfile('credentials')}
                    className="w-full h-24 px-8 py-2 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none mb-6"
                    placeholder="Help us verify your work — share notable achievements, press coverage, or links to your music industry credits and profiles."
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" variant="outline" className="w-34">
                    Submit
                  </Button>
                </div>
              </form>
            )}
    
            {verificationRequest?.status === 'pending' && (
              <div className="bg-surface-light rounded-full p-4 mb-6">
                <p className="text-neutral text-sm">
                  Your verification request is being reviewed.
                </p>
                <p className="text-neutral text-sm mt-2">
                  Submitted: {format(new Date(verificationRequest.submission_date), 'PPP')}
                </p>
              </div>
            )}
    
            {verificationRequest?.status === 'rejected' && (
              <div className="space-y-4 mb-6">
                <div className="bg-surface-light rounded-full py-4 px-8">
                  <p className="text-neutral text-sm">
                    Your verification request was rejected.
                    {verificationRequest.rejection_reason && (
                      <>
                        <br />
                        <span className="text-base">
                          Reason: {verificationRequest.rejection_reason}
                        </span>
                      </>
                    )}
                  </p>
                  <p className="text-neutral text-sm mt-2">
                    Reviewed: {format(new Date(verificationRequest.review_date!), 'PPP')}
                  </p>
                </div>
              </div>
            )}
          </Card>
          <Card className="bg-neutral mb-6">
            <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Account Details</h2>
            <div className="space-y-4">
              <div className="flex items-center text-surface-light">
                <Calendar className="w-5 h-5 text-primary mr-2" />
                <span className="font-dillan">
                  Joined {format(new Date(user.created_at), 'MMMM yyyy')}
                </span>
              </div>
              
              {/* Account Deletion Section */}
              <div className="border-t border-primary pt-4 mt-6">
                <h3 className="text-xl font-semibold text-secondary-light mb-4">Delete Account</h3>
                <div className="flex items-center text-surface-light">
                  <Trash2 
                    className="w-5 h-5 text-primary mr-2 cursor-pointer hover:text-secondary transition-colors" 
                    onClick={() => setShowDeleteModal(true)}
                  />
                  <span className="font-dillan">Permanently delete your account</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Feedback Component */}
          <Card className="bg-neutral mb-6">
            <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6 flex items-center">
              Contact Us
            </h2>

            <div className="space-y-4">
              {/* Question Section */}
              <div>
                
                <div className="relative">
                  <textarea
                    value={questionContent}
                    onChange={(e) => setQuestionContent(e.target.value)}
                    placeholder="Have a question? We'll get back to you by email!"
                    className="w-full h-20 px-8 py-3 pr-12 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none"
                  />
                  <button
                    onClick={() => handleSubmitContent('question', questionContent)}
                    disabled={isSendingQuestion || !questionContent.trim()}
                    className="absolute bottom-3 right-8 p-2 rounded-full bg-secondary text-surface-light hover:text-primary hover:bg-primary-light transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                {questionSuccess && (
                  <p className="mt-0 pl-4 text-sm text-surface-light flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Question sent successfully!
                  </p>
                )}
              </div>

              {/* Feedback Section */}
              <div>
               
                <div className="relative">
                  <textarea
                    value={feedbackContent}
                    onChange={(e) => setFeedbackContent(e.target.value)}
                    placeholder="Tell us about your experience or suggest new features!"
                    className="w-full h-20 px-8 py-3 pr-12 rounded-full border border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none"
                  />
                  <button
                    onClick={() => handleSubmitContent('feedback', feedbackContent)}
                    disabled={isSendingFeedback || !feedbackContent.trim()}
                    className="absolute bottom-3 right-8 p-2 rounded-full bg-secondary text-surface-light hover:text-primary hover:bg-primary-light transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                {feedbackSuccess && (
                  <p className="mt-0 pl-4 text-sm text-surface-light flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Feedback sent successfully!
                  </p>
                )}
              </div>

              {feedbackError && (
                <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark rounded-xl p-4 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <p>{feedbackError}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Account Details card remains the same... */}
        </>
      )}

      {/* Account Deletion Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-start justify-center p-4 z-10">
          <div className="bg-neutral rounded-xl w-full max-w-md">
            <div className="bg-base px-6 py-4 rounded-t-xl">
              <h2 className="text-3xl font-bold text-surface-light">Delete Account</h2>
            </div>
            
            <div className="p-4">
              <div className="text-sm text-surface-light">
                <p className="mb-2 text-surface-light">This action cannot be undone. This will permanently:</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Delete your profile and account</li>
                  <li>Remove all your posts and messages</li>
                  <li>Remove you from all communities</li>
                  <li>Delete all your uploaded files</li>
                  <li>Cancel any pending verification requests</li>
                </ul>
              </div>
              {deleteError && (
                <div className="mb-4 p-3 rounded-lg bg-secondary bg-opacity-20 text-secondary-dark text-xl flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{deleteError}</span>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary-light mb-2 pl-4">
                  Type "delete my account" to confirm:
                </label>
                <input
                  type="text-primary"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full h-12 px-4 rounded-full border border-surface-light bg-surface-light text-primary placeholder:text-primary focus:border-surface-light focus:ring-0 focus:outline-none mb-6"
                  placeholder="delete my account"
                  disabled={isDeletingAccount}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                  }}
                  disabled={isDeletingAccount}
                  className="flex-1 text-surface-light"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={
                    isDeletingAccount || 
                    deleteConfirmText.toLowerCase() !== 'delete my account'
                  }
                  className="flex-1 bg-base text-surface-light hover:bg-secondary hover:text-primary border-base hover:border-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingAccount ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-surface-light mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Rest of the component remains the same... */}
    </div>
  );
}

export default Profile;